import { useEffect, useState } from 'react';

import {
  CheckCircleFilled,
  ClockCircleFilled,
  DollarCircleOutlined,
  EditOutlined,
  GiftOutlined,
  PercentageOutlined,
  PieChartOutlined,
  PlayCircleFilled,
  StopFilled,
} from '@ant-design/icons';
import { Button, Col, Empty, PageHeader, Row, Select } from 'antd';
import 'moment/locale/pt-br';
import moment from 'moment';
import { connect, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { Link } from 'react-router-dom';
import { T } from 'react-translator-component';

import * as S from './styled';

import { getVoucherDetail, getVoucherHistory } from '../../../hooks/vouchers';
import { setUsersVoucherList } from '../../../redux/actions/voucher';
import { formatFullDate } from '../../../utils/dates';
import { COLORS } from '../../../utils/theme/colors';
import ChargerIcon from '../../atoms/icons/ChargerIcon';
import VoucherIcon from '../../atoms/icons/VoucherIcon';
import { ListSkeleton } from '../../molecules/ListSkeleton/ListSkeleton';
import VoucherSelectedUsersList from '../../organisms/VoucherSelectedUsersList/VoucherSelectedUsersList';

moment.locale('pt-br');

const rawMaskMoney = (value: string) => {
  const onlyDigits = value
    .split('')
    .filter((s) => /\d/.test(s))
    .join('')
    .padStart(3, '0');

  const digitsFloat = onlyDigits.slice(0, -2) + '.' + onlyDigits.slice(-2);

  return digitsFloat;
};

const mascaraMoeda = (value: any) => {
  const digitsFloat = rawMaskMoney(value);

  return maskCurrency(digitsFloat);
};

const maskCurrency = (valor, locale = 'pt-BR', currency = 'BRL') => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(valor);
};

const formatTransactionTimeStamp = (timeStamp: string) => {
  const month = moment(timeStamp)
    .format('MMMM')
    .replace(/^\w/, (c) => c.toUpperCase());
  const day = moment(timeStamp)
    .format('D')
    .replace(/^\w/, (c) => c.toUpperCase());
  return `${day} de ${month} às${formatFullDate(timeStamp).split('s')[1]}`;
};

const getChargerDescriptions = (chargeBoxPks: any, chargePointsList: any) => {
  const descriptions: any = [];

  chargeBoxPks.map((chargeBoxPk: number) => {
    const [chargePointList] = chargePointsList.filter(
      (chargePointList) => chargePointList.chargeBoxPk === chargeBoxPk,
    );

    if (chargePointList) {
      descriptions.push(
        `${chargePointList.chargeBoxId}${
          chargePointList.description ? ` - ${chargePointList.description}` : ''
        }`,
      );
    }
  });

  return descriptions.join(', ');
};

const VoucherDetailPage = ({ chargePointsList }: any) => {
  const { id }: any = useParams();
  const history = useHistory();
  const dispatch = useDispatch();

  const [voucherDetail, setVoucherDetail] = useState<any>();
  const [voucherHistory, setVoucherHistory] = useState<any>();
  const [voucherUsers, setVouchersUsers] = useState<any[]>();
  const [filteredVoucherHistory, setFilteredVoucherHistory] = useState<any[]>();

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);

      const responseDetail = await getVoucherDetail(id);

      setVoucherDetail(responseDetail);

      const responseHistory: any = await getVoucherHistory(id);

      const uniqueUsersSet = new Set(
        responseHistory.results.map((item) => JSON.stringify(item.user)),
      );

      const uniqueUsersArray = Array.from(uniqueUsersSet).map((user: any) =>
        JSON.parse(user),
      );

      setVouchersUsers(uniqueUsersArray);

      if (responseHistory) {
        const sortedResult = responseHistory.results.sort((a, b) => {
          if (
            moment().isAfter(
              a.transaction_start_timestamp,
              b.transaction_start_timestamp,
            )
          ) {
            return -1;
          }

          if (
            moment().isBefore(
              a.transaction_start_timestamp,
              b.transaction_start_timestamp,
            )
          ) {
            return 1;
          }

          return 0;
        });

        setVoucherHistory(sortedResult);

        setFilteredVoucherHistory(
          sortedResult.filter(
            (value) => value.user.userPk === uniqueUsersArray[0].userPk,
          ),
        );
      }

      setLoading(false);
    })();
  }, [id]);

  const getKind = (kind) => {
    const kinds = {
      PERCENT_RECHARGE: {
        icon: <PercentageOutlined style={{ marginRight: 8 }} />,
        text: 'Porcentagem',
      },
      MONEY_RECHARGE: {
        icon: <DollarCircleOutlined style={{ marginRight: 8 }} />,
        text: 'Reais',
      },
      KWH_RECHARGE: {
        icon: (
          <ChargerIcon
            fill='rgba(0, 0, 0, 0.7)'
            style={{ marginRight: 8, position: 'relative', top: 2 }}
          />
        ),
        text: 'Quilowatt-hora',
      },
    };

    return (
      <>
        {kinds[kind].icon}
        {kinds[kind].text}
      </>
    );
  };

  function getUsersWhoActivedVoucher() {
    const usersActivationWithVoucherStatus =
      voucherDetail?.voucher_users_pks_with_voucher_status?.filter(
        (user) => user.source === 'ACTIVATION',
      );
    return usersActivationWithVoucherStatus;
  }
  function getUsersGuestVoucher() {
    const usersGuestWithVoucherStatus =
      voucherDetail?.voucher_users_pks_with_voucher_status?.filter(
        (user) => user.source === 'GUEST',
      );
    return usersGuestWithVoucherStatus;
  }

  const voucherValue = (type, value: number) => {
    switch (type) {
      case 'PERCENT_RECHARGE':
        return `${value.toFixed(2)}%`;
      case 'KWH_RECHARGE':
        return `${value.toFixed(2)} kWh`;
      default:
        return `R$${value.toFixed(2)}`;
    }
  };

  return (
    <S.Wrapper>
      <PageHeader
        onBack={() => history.push('/vouchers')}
        title={
          <>
            <VoucherIcon
              style={{ position: 'relative', top: 3, marginRight: 16 }}
              fill={COLORS.black}
              height={20}
              width={20}
            />

            {voucherDetail?.code?.toUpperCase()}
          </>
        }
      />
      <S.Blank>
        <S.Header>
          Detalhes
          <Link to={`/voucher/${id}/edit`}>
            <Button
              type='text'
              onClick={() =>
                dispatch(
                  setUsersVoucherList(
                    voucherDetail?.voucher_users_pks_with_voucher_status,
                  ),
                )
              }
              icon={<EditOutlined />}
            >
              Editar
            </Button>
          </Link>
        </S.Header>

        <Row>
          <Col span={12}>
            <div
              style={{ display: 'flex', alignItems: 'center', marginRight: 24 }}
            >
              <div style={{ fontWeight: 300, marginRight: 8 }}>Tipo:</div>{' '}
              {voucherDetail?.kind && getKind(voucherDetail?.kind)}
            </div>
          </Col>

          <Col span={12}>
            <div
              style={{ display: 'flex', alignItems: 'center', marginRight: 24 }}
            >
              <div style={{ fontWeight: 300, marginRight: 8 }}>Valor:</div>
              {voucherDetail?.kind === 'MONEY_RECHARGE'
                ? mascaraMoeda(Number(voucherDetail?.kind_value).toFixed(2))
                : voucherDetail?.kind === 'PERCENT_RECHARGE'
                ? `${voucherDetail?.kind_value}%`
                : voucherDetail?.kind === 'KWH_RECHARGE'
                ? `${voucherDetail?.kind_value}kWh`
                : ''}
            </div>
          </Col>
        </Row>

        <Row style={{ marginTop: 24 }}>
          <Col span={12}>
            <div
              style={{ display: 'flex', alignItems: 'center', marginRight: 24 }}
            >
              <div style={{ fontWeight: 300, marginRight: 8 }}>
                Nome do Voucher:
              </div>{' '}
              {voucherDetail?.title}
            </div>
          </Col>
          <Col span={12}>
            <div
              style={{ display: 'flex', alignItems: 'center', marginRight: 24 }}
            >
              <div style={{ fontWeight: 300, marginRight: 8 }}>Descrição:</div>{' '}
              {voucherDetail?.description}
            </div>
          </Col>
        </Row>

        <Row style={{ marginTop: 24 }}>
          <Col span={12}>
            <div
              style={{ display: 'flex', alignItems: 'center', marginRight: 24 }}
            >
              <div style={{ fontWeight: 300, marginRight: 8 }}>Início em:</div>{' '}
              {voucherDetail?.start &&
                moment(voucherDetail?.start).format('DD/MM/YYYY HH:mm:ss')}
            </div>
          </Col>
          <Col span={12}>
            <div
              style={{ display: 'flex', alignItems: 'center', marginRight: 24 }}
            >
              <div style={{ fontWeight: 300, marginRight: 8 }}>Validade:</div>{' '}
              {voucherDetail?.expire &&
                moment(voucherDetail?.expire).format('DD/MM/YYYY HH:mm:ss')}
            </div>
          </Col>
        </Row>

        <Row style={{ marginTop: 24 }}>
          <Col span={12}>
            <div
              style={{ display: 'flex', alignItems: 'center', marginRight: 24 }}
            >
              <div style={{ fontWeight: 300, marginRight: 8 }}>Criado por:</div>{' '}
              {voucherDetail?.created_user_name}
            </div>
          </Col>
          <Col span={12}>
            <div
              style={{ display: 'flex', alignItems: 'center', marginRight: 24 }}
            >
              <div style={{ fontWeight: 300, marginRight: 8 }}>Status:</div>{' '}
              {voucherDetail?.status && getStatus(voucherDetail?.status)}
            </div>
          </Col>
        </Row>

        <Row style={{ marginTop: 24 }}>
          <Col span={24}>
            <div
              style={{ display: 'flex', alignItems: 'center', marginRight: 24 }}
            >
              <div style={{ fontWeight: 300, marginRight: 8 }}>
                Carregadores:
              </div>
              {voucherDetail?.charge_box_pks?.length
                ? getChargerDescriptions(
                    voucherDetail?.charge_box_pks,
                    chargePointsList,
                  )
                : loading
                ? 'Carregando...'
                : 'Todos'}
            </div>
          </Col>
        </Row>
      </S.Blank>

      <S.Blank style={{ marginTop: 24 }}>
        <Row>
          <S.Header>Indicadores</S.Header>
        </Row>

        <Row style={{ justifyContent: 'space-between' }}>
          <S.Indicator>
            <S.IndicatorIcon>
              <VoucherIcon fill='#fff' width={30} height={30} />
            </S.IndicatorIcon>
            <div>
              <div style={{ fontWeight: 300 }}>Quantidade total</div>
              <div
                style={{
                  fontSize: 30,
                  lineHeight: '30px',
                  textAlign: 'center',
                }}
              >
                {voucherDetail?.total}
              </div>
            </div>
          </S.Indicator>

          <S.Indicator>
            <S.IndicatorIcon>
              <PieChartOutlined style={{ color: 'white', fontSize: 24 }} />
            </S.IndicatorIcon>
            <div>
              <div style={{ fontWeight: 300 }}>Quantidade consumida</div>
              <div
                style={{
                  fontSize: 30,
                  lineHeight: '30px',
                  textAlign: 'center',
                }}
              >
                {voucherDetail?.used_total}
              </div>
            </div>
          </S.Indicator>

          <S.Indicator>
            <S.IndicatorIcon>
              <GiftOutlined style={{ color: 'white', fontSize: 24 }} />
            </S.IndicatorIcon>
            <div>
              <div style={{ fontWeight: 300 }}>Voucher disponíveis</div>
              <div
                style={{
                  fontSize: 30,
                  lineHeight: '30px',
                  textAlign: 'center',
                }}
              >
                {voucherDetail?.not_used_total}
              </div>
            </div>
          </S.Indicator>
        </Row>
      </S.Blank>
      {voucherDetail !== undefined &&
        getUsersWhoActivedVoucher().length >= 0 &&
        (getUsersGuestVoucher().length > 0 ? (
          <></>
        ) : (
          <>
            <S.Blank style={{ marginTop: 24 }}>
              <Row>
                <S.Header>Usuarios com Vouchers Ativados</S.Header>
              </Row>
              <Row>
                <VoucherSelectedUsersList
                  userSelectedPks={voucherDetail?.user_pks}
                />
              </Row>
            </S.Blank>
          </>
        ))}

      {voucherDetail !== undefined && getUsersGuestVoucher()?.length > 0 && (
        <S.Blank style={{ marginTop: 24 }}>
          <Row>
            <S.Header>
              {T('Guests')}
              <Button
                onClick={() => {
                  history.push(`/vouchers/edit/${id}/select_users`, {
                    userSelectedPks:
                      voucherDetail?.voucher_users_pks_with_voucher_status,
                  });
                }}
                type='text'
                icon={<EditOutlined />}
              >
                Editar
              </Button>
            </S.Header>
          </Row>
          <Row>
            <VoucherSelectedUsersList
              userSelectedPks={voucherDetail?.user_pks}
            />
          </Row>
        </S.Blank>
      )}

      <S.Blank style={{ marginTop: 24 }}>
        <Row>
          <S.Header>Histórico de atividades</S.Header>

          {loading ? (
            <ListSkeleton />
          ) : (
            <>
              {!voucherHistory?.length ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description='Não há histórico disponível para este voucher'
                  style={{ margin: 'auto', width: '100%' }}
                />
              ) : (
                <S.ListArea>
                  {voucherHistory?.length !== 0 && (
                    <>
                      <S.SelectContainer>
                        <Select
                          showSearch
                          style={{ width: '390px' }}
                          placeholder={T('Select users')}
                          onChange={(userId: number) => {
                            setFilteredVoucherHistory(
                              voucherHistory.filter(
                                (value) => value.user.userPk === userId,
                              ),
                            );
                          }}
                          defaultValue={voucherUsers && voucherUsers[0]?.userPk}
                          filterOption={(input, option) => {
                            const user = [0, 2]
                              .map((e) => option?.children[e])
                              .join('-');
                            return user
                              ?.toLowerCase()
                              .includes(input?.toLowerCase());
                          }}
                          size='large'
                        >
                          {voucherUsers?.length &&
                            voucherUsers?.map((user, index) => {
                              return (
                                <Select.Option value={user.userPk} key={index}>
                                  {user.email} - {user.name}
                                </Select.Option>
                              );
                            })}
                        </Select>
                      </S.SelectContainer>

                      <S.TimeLine progressDot direction='vertical'>
                        {filteredVoucherHistory?.map((history) => {
                          return (
                            <S.TimeLineItem
                              color={COLORS.primary}
                              key={history.transaction_pk}
                            >
                              <>
                                <S.TimeLineTitle>
                                  {formatTransactionTimeStamp(
                                    history.transaction_start_timestamp,
                                  )}
                                </S.TimeLineTitle>

                                <S.TimeLineContent>
                                  <S.TimeLineContentHeader>
                                    <S.TimeLineContentTitle>
                                      Recarga
                                    </S.TimeLineContentTitle>
                                    <S.TimeLineContentRechargeValue>
                                      {voucherValue(
                                        history.voucher.kind,
                                        history.voucher.kind_value_used ||
                                          history.voucher.kind_value,
                                      )}
                                    </S.TimeLineContentRechargeValue>
                                  </S.TimeLineContentHeader>

                                  <S.TimeLineContentChargeBoxName>
                                    {history.charge_box_name}
                                  </S.TimeLineContentChargeBoxName>

                                  <S.TimeLineContentUser>
                                    {history.user.name}{' '}
                                    {!!history.user.email &&
                                      `- ${history.user.email}`}
                                  </S.TimeLineContentUser>

                                  {!!history.address &&
                                    !!history.address.street && (
                                      <S.TimeLineContentAddress>
                                        {`${history.address.street} ${
                                          history.address.houseNumber
                                            ? ', '
                                            : ''
                                        }`}
                                        {history.address.houseNumber}{' '}
                                        {!!history.address.district &&
                                          `- ${history.address.district}`}
                                      </S.TimeLineContentAddress>
                                    )}
                                </S.TimeLineContent>

                                {history.voucher.kind_value_balance !==
                                  null && (
                                  <S.BalanceDay>
                                    Saldo:{' '}
                                    {voucherValue(
                                      history.voucher.kind,
                                      history.voucher.kind_value_balance,
                                    )}
                                  </S.BalanceDay>
                                )}
                              </>
                            </S.TimeLineItem>
                          );
                        })}
                      </S.TimeLine>
                    </>
                  )}
                </S.ListArea>
              )}
            </>
          )}
        </Row>
      </S.Blank>
    </S.Wrapper>
  );
};

const mapStateToProps = (state) => ({
  chargePointsList: state.chargePoints.chargePoints.chargePointList,
});

export default connect(mapStateToProps)(VoucherDetailPage);

const getStatus = (statusEnum: string) => {
  const statusDescrption = {
    NOT_STARTED: {
      icon: (
        <PlayCircleFilled
          style={{ color: COLORS.carBrandUsage, marginRight: 8 }}
        />
      ),
      text: 'Não iniciado',
    },
    ACTIVE: {
      icon: (
        <CheckCircleFilled
          style={{ color: COLORS.available, marginRight: 8 }}
        />
      ),
      text: 'Ativo',
    },
    EXPIRED: {
      icon: <StopFilled style={{ color: COLORS.busy, marginRight: 8 }} />,
      text: 'Expirado',
    },
    INACTIVE: {
      icon: <StopFilled style={{ color: COLORS.busy, marginRight: 8 }} />,
      text: 'Inativo',
    },
    USED: {
      icon: (
        <ClockCircleFilled style={{ color: COLORS.consume, marginRight: 8 }} />
      ),
      text: 'Usado',
    },
  };

  return (
    <>
      {statusDescrption[statusEnum].icon} {statusDescrption[statusEnum].text}
    </>
  );
};
